import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Coins, ChevronDown, ChevronUp, Users, AlertTriangle, Loader2, Check, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { useSafeMode } from '../contexts/SafeModeContext';
import { tokenApi } from '../services/api';
import { toast } from 'sonner';

/**
 * TokenManagement Component
 * 
 * Mini dashboard for AI token budget in Billing tab.
 * Features:
 * - Budget breakdown (Plan Allotment + Buy-up + Bonus)
 * - Usage percentage bar
 * - Top user (expandable to all users)
 * - Buy more tokens (500K, 1M, 2M)
 * - Cancel/reduce buy-up
 */
const TokenManagement = ({ userRole, refreshKey }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [loading, setLoading] = useState(true);
  const [tokenStatus, setTokenStatus] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [topUser, setTopUser] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [allUsersUsage, setAllUsersUsage] = useState([]);
  
  // Buy-up modals
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [buyPreview, setBuyPreview] = useState(null);
  const [processingBuy, setProcessingBuy] = useState(false);
  
  // Cancel modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [processingCancel, setProcessingCancel] = useState(false);

  const isBillingManager = userRole === 'owner' || userRole === 'billing_manager' || userRole === 'admin';

  useEffect(() => {
    fetchData();
  }, [refreshKey]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, tiersRes] = await Promise.all([
        tokenApi.getStatus(),
        tokenApi.getTiers()
      ]);
      
      setTokenStatus(statusRes.data);
      setTiers(tiersRes.data.tiers || []);
      
      // Fetch top user if billing manager
      if (isBillingManager) {
        try {
          const topRes = await tokenApi.getTopUser();
          setTopUser(topRes.data.top_user);
        } catch (e) {
          console.log('No top user data');
        }
      }
    } catch (err) {
      console.error('Failed to fetch token data:', err);
      toast.error('Failed to load token information');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsersUsage = async () => {
    try {
      const res = await tokenApi.getUsage();
      setAllUsersUsage(res.data.by_user || []);
    } catch (err) {
      console.error('Failed to fetch usage breakdown:', err);
    }
  };

  const handleExpandUsers = () => {
    if (!expanded) {
      fetchAllUsersUsage();
    }
    setExpanded(!expanded);
  };

  const handleSelectTier = async (tier) => {
    setSelectedTier(tier);
    setProcessingBuy(true);
    
    try {
      const res = await tokenApi.previewBuyup(tier.tier_id);
      setBuyPreview(res.data);
      setShowBuyModal(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to get pricing preview');
    } finally {
      setProcessingBuy(false);
    }
  };

  const handleBuyTokens = async () => {
    if (!selectedTier) return;
    
    setProcessingBuy(true);
    try {
      const currentUrl = window.location.href;
      const res = await tokenApi.checkoutBuyup(
        selectedTier.tier_id,
        `${currentUrl}?token_success=true`,
        `${currentUrl}?token_cancel=true`
      );
      
      if (res.data.type === 'checkout') {
        // Redirect to Stripe checkout (no payment method on file)
        window.location.href = res.data.checkout_url;
      } else if (res.data.type === 'subscription_created') {
        // Subscription created directly (payment method already on file)
        toast.success(res.data.message);
        setShowBuyModal(false);
        fetchData();
      } else if (res.data.type === 'subscription_updated') {
        // Subscription was updated directly (upgrade)
        toast.success(res.data.message);
        setShowBuyModal(false);
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setProcessingBuy(false);
    }
  };

  const handleCancelBuyup = async () => {
    setProcessingCancel(true);
    try {
      const res = await tokenApi.cancelBuyup();
      toast.success(res.data.message);
      setShowCancelModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel buy-up');
    } finally {
      setProcessingCancel(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num?.toString() || '0';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ok': return 'text-green-600 dark:text-green-400';
      case 'warning': return 'text-amber-600 dark:text-amber-400';
      case 'critical': return 'text-red-600 dark:text-red-400';
      case 'exceeded': return 'text-red-700 dark:text-red-500';
      default: return colors.textMuted;
    }
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return 'bg-red-500';
    if (percent >= 80) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  if (loading) {
    return (
      <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </Card>
    );
  }

  const breakdown = tokenStatus?.breakdown || {};
  const buyup = tokenStatus?.buyup || {};
  const budget = tokenStatus?.budget || 0;
  const used = tokenStatus?.used || 0;
  const remaining = tokenStatus?.remaining || 0;
  const percentUsed = tokenStatus?.percent_used || 0;

  return (
    <>
      <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={cn("text-lg font-semibold flex items-center gap-2", colors.textPrimary)}>
            <Coins className="w-5 h-5 text-emerald-500" />
            AI Token Budget
          </h3>
          {tokenStatus?.status && tokenStatus.status !== 'no_budget' && (
            <Badge 
              variant="outline"
              className={cn(
                "capitalize",
                tokenStatus.status === 'ok' && "border-green-500 text-green-600",
                tokenStatus.status === 'warning' && "border-amber-500 text-amber-600",
                tokenStatus.status === 'critical' && "border-red-500 text-red-600",
                tokenStatus.status === 'exceeded' && "border-red-700 text-red-700"
              )}
            >
              {tokenStatus.status}
            </Badge>
          )}
        </div>

        {/* Budget Breakdown */}
        {isBillingManager && budget > 0 && (
          <div className={cn("p-4 rounded-lg border mb-4", colors.bgTertiary, colors.borderPrimary)}>
            <p className={cn("text-sm font-medium mb-2", colors.textSecondary)}>Monthly Budget</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className={colors.textMuted}>Plan Allotment</span>
                <span className={colors.textPrimary}>{formatNumber(breakdown.plan_allotment)}</span>
              </div>
              {buyup.amount > 0 && (
                <div className="flex justify-between">
                  <span className={colors.textMuted}>Token Buy-up</span>
                  <span className="text-emerald-600">+{formatNumber(buyup.amount)}</span>
                </div>
              )}
              {breakdown.bonus_amount > 0 && (
                <div className="flex justify-between">
                  <span className={colors.textMuted}>Bonus</span>
                  <span className="text-purple-600">+{formatNumber(breakdown.bonus_amount)}</span>
                </div>
              )}
              <div className={cn("flex justify-between pt-2 border-t font-medium", colors.borderPrimary)}>
                <span className={colors.textPrimary}>Total</span>
                <span className={colors.textPrimary}>{formatNumber(budget)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Usage Progress */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className={cn("text-sm", colors.textSecondary)}>Usage This Month</span>
            <span className={cn("text-sm font-medium", getStatusColor(tokenStatus?.status))}>
              {percentUsed}% used
            </span>
          </div>
          <Progress 
            value={Math.min(percentUsed, 100)} 
            className="h-3"
            indicatorClassName={getProgressColor(percentUsed)}
          />
          <div className="flex justify-between mt-1">
            <span className={cn("text-xs", colors.textMuted)}>
              {formatNumber(used)} used
            </span>
            <span className={cn("text-xs", colors.textMuted)}>
              {formatNumber(remaining)} remaining
            </span>
          </div>
        </div>

        {/* Top User (mini) */}
        {isBillingManager && topUser && (
          <div 
            className={cn(
              "p-3 rounded-lg border cursor-pointer hover:bg-opacity-80 transition-colors mb-4",
              colors.bgTertiary, colors.borderPrimary
            )}
            onClick={handleExpandUsers}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                <span className={cn("text-sm", colors.textSecondary)}>Top User:</span>
                <span className={cn("text-sm font-medium", colors.textPrimary)}>{topUser.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn("text-sm", colors.textMuted)}>
                  {topUser.percent_of_total}% of usage
                </span>
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
            
            {/* Expanded users list */}
            {expanded && allUsersUsage.length > 0 && (
              <div className="mt-3 pt-3 border-t space-y-2">
                {allUsersUsage.map((u, idx) => (
                  <div key={u._id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className={cn("w-5 text-center font-medium", colors.textMuted)}>#{idx + 1}</span>
                      <span className={colors.textPrimary}>{u.user_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={colors.textMuted}>{formatNumber(u.total_tokens)} tokens</span>
                      <span className={cn("text-xs px-2 py-0.5 rounded", colors.bgSecondary)}>
                        {u.request_count} requests
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Buy More Tokens */}
        {isBillingManager && (
          <div className={cn("p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
            <div className="flex items-center justify-between mb-3">
              <span className={cn("text-sm font-medium", colors.textPrimary)}>
                {buyup.amount > 0 ? 'Upgrade Token Buy-up' : 'Buy More Tokens'}
              </span>
              {buyup.tier && (
                <Badge variant="outline" className="border-emerald-500 text-emerald-600">
                  Current: {formatNumber(buyup.amount)}/mo
                </Badge>
              )}
            </div>
            
            <div className="grid grid-cols-3 gap-2 mb-3">
              {tiers.map(tier => (
                <Button
                  key={tier.tier_id}
                  variant="outline"
                  size="sm"
                  onClick={() => handleSelectTier(tier)}
                  disabled={processingBuy}
                  className={cn(
                    "flex flex-col h-auto py-2",
                    buyup.tier === tier.tier_id && "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20"
                  )}
                >
                  <span className="font-medium">{tier.label}</span>
                  <span className={cn("text-xs", colors.textMuted)}>{tier.price_display}</span>
                </Button>
              ))}
            </div>
            
            {/* Cancel Buy-up */}
            {buyup.tier && (
              <div className="flex justify-between items-center pt-3 border-t">
                <div className="text-sm">
                  {buyup.cancel_scheduled ? (
                    <span className="text-amber-600">
                      Cancels on {new Date(buyup.cancel_date).toLocaleDateString()}
                    </span>
                  ) : (
                    <span className={colors.textMuted}>
                      Buy-up renews monthly
                    </span>
                  )}
                </div>
                {!buyup.cancel_scheduled && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCancelModal(true)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Cancel Buy-up
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* No budget configured message */}
        {budget === 0 && (
          <div className={cn("p-4 rounded-lg border text-center", colors.bgTertiary, colors.borderPrimary)}>
            <p className={cn("text-sm", colors.textMuted)}>
              No AI token budget configured for your plan.
            </p>
            {isBillingManager && tiers.length > 0 && (
              <p className={cn("text-sm mt-2", colors.textSecondary)}>
                Purchase a token buy-up to enable AI features.
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Buy Preview Modal */}
      <Dialog open={showBuyModal} onOpenChange={setShowBuyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-emerald-500" />
              {buyPreview?.action === 'upgrade' ? 'Upgrade Token Buy-up' : 'Add Token Buy-up'}
            </DialogTitle>
            <DialogDescription>
              {selectedTier?.label} - {selectedTier?.description}
            </DialogDescription>
          </DialogHeader>

          {buyPreview && (
            <div className="py-4 space-y-4">
              {/* What you'll get */}
              <div className={cn("p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                <p className={cn("text-sm font-medium mb-2", colors.textPrimary)}>What You&apos;ll Get</p>
                <div className="space-y-2 text-sm">
                  {buyPreview.action === 'upgrade' && (
                    <div className="flex justify-between">
                      <span className={colors.textMuted}>Current</span>
                      <span className={colors.textPrimary}>{formatNumber(buyPreview.current_tokens)}/month</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className={colors.textMuted}>New Amount</span>
                    <span className="text-emerald-600 font-medium">{formatNumber(buyPreview.new_tokens)}/month</span>
                  </div>
                  {buyPreview.additional_tokens > 0 && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Additional</span>
                      <span>+{formatNumber(buyPreview.additional_tokens)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing */}
              <div className={cn("p-4 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                <p className={cn("text-sm font-medium mb-2", colors.textPrimary)}>Pricing</p>
                <div className="space-y-2 text-sm">
                  {buyPreview.action === 'upgrade' && buyPreview.prorated_charge_cents > 0 && (
                    <div className="flex justify-between">
                      <span className={colors.textMuted}>Due Today (prorated)</span>
                      <span className={colors.textPrimary}>{buyPreview.prorated_charge_display}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-medium">
                    <span className={colors.textPrimary}>Monthly Price</span>
                    <span className={colors.textPrimary}>{buyPreview.new_monthly_price_display}</span>
                  </div>
                </div>
              </div>

              <p className={cn("text-xs", colors.textMuted)}>
                {buyPreview.effective_immediately 
                  ? "✓ Tokens available immediately after purchase"
                  : `Tokens available on ${new Date(buyPreview.next_billing_date).toLocaleDateString()}`
                }
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBuyModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBuyTokens}
              disabled={processingBuy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {processingBuy ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Confirm Purchase</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Cancel Token Buy-up
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your token buy-up subscription?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className={cn("p-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800")}>
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Important:</strong> Your buy-up tokens ({formatNumber(buyup.amount)}/month) 
                will remain available until the end of your current billing period. 
                After that, they will not renew.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>
              Keep Buy-up
            </Button>
            <Button
              onClick={handleCancelBuyup}
              disabled={processingCancel}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {processingCancel ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Canceling...</>
              ) : (
                <><X className="w-4 h-4 mr-2" /> Cancel Buy-up</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TokenManagement;
